// Client-side grey-pixel transparency for FAA aeronautical chart tiles.
//
// Problem
// -------
// vfrmap.com returns a solid-grey JPEG (~4 KB, RGB #555555) for tiles
// outside chart coverage. Our edge proxy (api/tile.ts) already 404s those
// pure-grey tiles by byte-size threshold (<5500 b).
//
// BUT boundary tiles — where real chart content meets the chart's
// coverage edge — contain a mix of chart pixels and grey fill within
// the same JPEG. Those tiles are >5500 b and pass the proxy filter,
// leaving grey patches on top of the imagery basemap right where the
// VFR sectional ends.
//
// Solution
// --------
// Intercept chart tile loads with a MapLibre custom protocol. Decode
// the JPEG to an ImageBitmap, blit to an OffscreenCanvas, walk the
// pixels, and zero the alpha of any pixel that's near-grey #555.
// Return a transparent PNG ArrayBuffer to MapLibre. The non-grey
// chart pixels render exactly as before; the grey fringe disappears
// and the satellite / topo / OSM imagery shows through cleanly all
// the way to the published chart edge.
//
// All work runs on a worker-friendly OffscreenCanvas when available
// (~all modern browsers); we fall back to a regular HTMLCanvasElement
// otherwise.

import maplibregl from 'maplibre-gl';

// Tunables for grey detection. vfrmap's empty-region pixel is exactly
// #555555 (R=G=B=85). We accept anything achromatic with luminance in
// a tight band around that value, so:
//   - White (255) is not killed.
//   - Black map symbology is not killed (luminance too low).
//   - Tan/green/blue/magenta chart fills aren't killed (too much chroma).
//   - Real boundary grey IS killed.
const GREY_LUM_MIN  = 0x42;   // ~66
const GREY_LUM_MAX  = 0x6E;   // ~110
const GREY_CHROMA_MAX = 14;   // max(R,G,B) - min(R,G,B) tolerance

// Protocol scheme. Source URLs in style.ts use this scheme so MapLibre
// routes them through our handler instead of fetch().
export const CHART_PROTOCOL = 'vfrchart';

let registered = false;

/**
 * Register the custom protocol with MapLibre. Idempotent — safe to
 * call on every map mount; subsequent calls are no-ops.
 */
export function registerChartTransparencyProtocol(): void {
  if (registered) return;
  registered = true;

  maplibregl.addProtocol(CHART_PROTOCOL, async (params, abortController) => {
    // The MapLibre URL we registered comes in as "vfrchart://<real-url>".
    // Strip the scheme and fetch the real URL through the edge proxy.
    const realUrl = params.url.replace(/^vfrchart:\/\//, '');

    const resp = await fetch(realUrl, { signal: abortController.signal });
    if (!resp.ok) {
      // Propagate 404s (proxy-detected pure-grey tiles) so MapLibre
      // shows the imagery basemap underneath.
      throw new Error(`tile fetch ${resp.status}`);
    }

    const blob = await resp.blob();
    if (!blob.size) {
      throw new Error('empty tile');
    }

    try {
      const transparentPng = await keyOutGrey(blob);
      return {
        data: transparentPng,
        cacheControl: resp.headers.get('Cache-Control') ?? undefined,
        expires: resp.headers.get('Expires') ?? undefined,
      };
    } catch (err) {
      // On any decode/processing failure, fall back to the raw JPEG.
      // Better to show a grey-fringe tile than a broken one.
      // eslint-disable-next-line no-console
      console.warn('[chart-transparency] fallback to raw tile:', err);
      const ab = await blob.arrayBuffer();
      return {
        data: ab,
        cacheControl: resp.headers.get('Cache-Control') ?? undefined,
      };
    }
  });
}

/**
 * Decode a JPEG/PNG blob, mark near-grey pixels transparent, return a
 * PNG ArrayBuffer.
 */
async function keyOutGrey(blob: Blob): Promise<ArrayBuffer> {
  const bitmap = await createImageBitmap(blob);
  const w = bitmap.width;
  const h = bitmap.height;

  // Prefer OffscreenCanvas (no DOM dependency, worker-friendly). Fall
  // back to a detached HTMLCanvasElement.
  let canvas: OffscreenCanvas | HTMLCanvasElement;
  let ctx:
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null = null;

  if (typeof OffscreenCanvas !== 'undefined') {
    canvas = new OffscreenCanvas(w, h);
    ctx = canvas.getContext('2d', { willReadFrequently: true }) as
      | OffscreenCanvasRenderingContext2D
      | null;
  } else {
    canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    ctx = canvas.getContext('2d', { willReadFrequently: true });
  }
  if (!ctx) throw new Error('canvas 2d ctx unavailable');

  ctx.drawImage(bitmap as CanvasImageSource, 0, 0);
  bitmap.close?.();

  const imgData = ctx.getImageData(0, 0, w, h);
  const px = imgData.data; // Uint8ClampedArray RGBA

  // Walk pixels. Inline test to avoid per-pixel function call overhead;
  // a 256x256 tile is 65 536 iterations and runs every chart-tile load.
  for (let i = 0, n = px.length; i < n; i += 4) {
    const r = px[i];
    const g = px[i + 1];
    const b = px[i + 2];

    const max = r > g ? (r > b ? r : b) : g > b ? g : b;
    const min = r < g ? (r < b ? r : b) : g < b ? g : b;
    const chroma = max - min;
    if (chroma > GREY_CHROMA_MAX) continue;

    const lum = (r + g + b) / 3;
    if (lum < GREY_LUM_MIN || lum > GREY_LUM_MAX) continue;

    // Achromatic + within the grey luminance band → kill it.
    px[i + 3] = 0;
  }

  ctx.putImageData(imgData, 0, 0);

  // Encode back to PNG so MapLibre keeps the alpha channel. JPEGs
  // can't carry alpha so we must use PNG here.
  let outBlob: Blob;
  if ('convertToBlob' in canvas) {
    outBlob = await (canvas as OffscreenCanvas).convertToBlob({ type: 'image/png' });
  } else {
    outBlob = await new Promise<Blob>((resolve, reject) =>
      (canvas as HTMLCanvasElement).toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob null'))),
        'image/png',
      ),
    );
  }
  return await outBlob.arrayBuffer();
}
